import { useState } from "react";
import { AccountSectionDataToApi } from "../pages/Sellers/_AccountSection";
import { AddressSectionData } from "../pages/Sellers/_AddressSection";
import { ContactSectionData } from "../pages/Sellers/_ContactSection";
import { FinancialSectionData } from "../pages/Sellers/_FinancialSection";
import { GeneralInfoSectionData } from "../pages/Sellers/_GeneralInfoSection";
import { InvoiceSectionData } from "../pages/Sellers/_InvoiceSection";
import {
  AntifraudServiceSeller,
  SellerBlockData,
  SellerBlockBankingData,
  SellerData,
} from "../models/Sellers";
import { api, api_banking } from "../services/Api";
import { showToast } from "../utils";
import { cepApi } from "../services/CepApiConfig";

export function useSeller() {
  const [seller, setSeller] = useState<SellerData>({} as SellerData);
  const [testLoading, setTestLoading] = useState(false);
  const [activeLoading, setActiveLoading] = useState(false);
  const [sortSellers, setSortSellers] = useState("sort=id,desc");

  const getBankingAccounts = async (sellerId: string) => {
    console.log("=== Buscando contas Banking ===");
    console.log("Seller ID:", sellerId);
    try {
      const response = await api_banking.get(`accounts/${sellerId}`);
      console.log("Contas encontradas:", response.data);
      return response.data;
    } catch (error: any) {
      console.error("Erro ao buscar contas Banking:", error);
      throw error;
    }
  };

  const getSellerData = async (id?: string) => {
    if (id === undefined) return;
    try {
      const response = await api.get(`/sellers/${id}`);
      const invoiceResponse = await api.get(`/sellers/${id}/invoice-config`);
      const formattedResponse = {
        ...response.data,
        invoice: {
          ...invoiceResponse.data,
        },
      };
      setSeller(formattedResponse);
    } catch (error: any) {
      error?.response?.data?.errors?.map((error: any) =>
        showToast({
          type: "error",
          message: `${error?.code} - ${error?.description}`,
        })
      );
    }
  };

  const sellerInvoiceTest = async (id?: string) => {
    try {
      setTestLoading(true);
      await api.post(`/sellers/${id}/invoice-test`);
      window.location.reload();
    } catch (error: any) {
      error?.response?.data?.errors?.map((error: any) =>
        showToast({
          type: "error",
          message: `${error?.code} - ${error?.description}`,
        })
      );
    } finally {
      setTestLoading(false);
    }
  };

  const sellerActiveInvoice = async (id?: string, isActive?: boolean) => {
    setActiveLoading(true);
    try {
      if (isActive) {
        await api.patch(`/sellers/${id}/invoice-active`, {
          active: false,
        });
      } else {
        await api.patch(`/sellers/${id}/invoice-active`, {
          active: true,
        });
      }
      showToast({
        message: "Sucesso ao ativar a nota fiscal!",
        type: "success",
      });
    } catch (error: any) {
      error?.response?.data?.errors?.map((error: any) =>
        showToast({
          type: "error",
          message: `${error?.code} - ${error?.description}`,
        })
      );
    } finally {
      setActiveLoading(false);
    }
  };

  const patchAccount = async (
    accountData: AccountSectionDataToApi,
    id: string | undefined
  ) => {
    await api.patch(`sellers/${id}/accounts`, accountData);
  };
  const getCategory = async () => {
    return await api.get(`category/list`);
  };
  const patchAddress = async (
    addressData: AddressSectionData,
    id: string | undefined
  ) => {
    await api.patch(`sellers/${id}/address`, addressData);
  };

  const patchBasic = async (
    generalInformationData: GeneralInfoSectionData,
    id: string | undefined
  ) => {
    await api.patch(`sellers/${id}/basic`, generalInformationData);
  };

  const patchIcon = (id: string | undefined): Promise<any> => {
    return api.patch(`/sellers/${id}/icon`);
  };

  const patchContacts = async (
    contactData: ContactSectionData,
    id: string | undefined
  ) => {
    await api.patch(`sellers/${id}/contacts`, contactData);
  };

  const patchPreApprovedValues = async (
    financialData: FinancialSectionData,
    id: string | undefined
  ) => {
    await api.patch(`sellers/${id}/pre-approved-values`, financialData);
  };

  const patchInvoice = async (
    invoiceData: InvoiceSectionData,
    id: string | undefined
  ) => {
    await api.patch(`sellers/${id}/invoice-config`, invoiceData);
  };

  const consultCep = (cep: string) => {
    return cepApi.get(`${cep}/json`);
  };

  const impersonateSeller = async (sellerId: string, userEmail: string) => {
    const response = await api.post("backoffice/v1/impersonate", {
      sellerId: Number(sellerId),
      userEmail,
    });
    return response.data;
  };

  const patchBlockUser = (sellerId: string, data: SellerBlockData) => {
    return api.patch(`support/seller/${sellerId}/block`, data);
  };

  const patchBlockBanking = (
    sellerId: string,
    data: SellerBlockBankingData
  ) => {
    console.log("=== patchBlockBanking chamado ===");
    console.log("URL:", `accounts/block/${sellerId}`);
    console.log("Data:", data);
    return api_banking.patch(`accounts/block/${sellerId}`, data);
  };

  const patchGhostMode = (sellerId: string, ghostModeEnabled: boolean) => {
    return api.put(
      `sellers/ghost-mode?idSeller=${sellerId}&enabled=${ghostModeEnabled}`
    );
  };

  const patchAntifraud = (sellerId: string, antiFraudActive: boolean) => {
    return api.put(
      `sellers/active-antifraud?idSeller=${sellerId}&enabled=${antiFraudActive}`
    );
  };

  const patchAntifraudService = (
    sellerId: number,
    antifraudService: AntifraudServiceSeller
  ) => api.patch(`sellers/antifraud-service/${sellerId}`, { antifraudService });
  const postSeller = (newSellerData: any) => {
    return api.post(`v3/sellers`, newSellerData);
  };

  const getTiers = async () => {
    return await api.get(`backoffice/v1/sellers/tiers`);
  };
  const patchTiers = async (tier: string, sellerId: string | undefined) => {
    return await api.patch(`backoffice/v1/sellers/${sellerId}/tier`, { tier });
  };
  const patchActiveBanking = async (
    sellerId: string,
    activeBanking: boolean
  ) => {
    return await api.patch(
      `sellers/${sellerId}/active-banking?active=${activeBanking}`
    );
  };

  const sellerCnpjSearch = async (cnpj: string) => {
    return await api.get(`cnpj/${cnpj}`);
  };

  const getOwners = async (idSeller: string | undefined) => {
    if (!idSeller) return;
    return await api.get(`v4/sellers/${idSeller}/owners`);
  };

  const getOwner = async (idSeller: string | undefined, idOwner: string | undefined) => {
    if (!idSeller || !idOwner) return;
    return await api.get(`v4/sellers/${idSeller}/owners/${idOwner}`);
  };

  const postOwner = async (idSeller: string | undefined, ownerData: any) => {
    if (!idSeller) return;
    return await api.post(`v4/sellers/${idSeller}/owners`, ownerData);
  };

  const patchOwner = async (idSeller: string | undefined, idOwner: string | undefined, ownerData: any) => {
    if (!idSeller || !idOwner) return;
    return await api.patch(`v4/sellers/${idSeller}/owners/${idOwner}`, ownerData);
  };

  const deleteOwner = async (idSeller: string | undefined, idOwner: string | undefined) => {
    if (!idSeller || !idOwner) return;
    return await api.delete(`v4/sellers/${idSeller}/owners/${idOwner}`);
  };

  return {
    seller,
    testLoading,
    activeLoading,
    getSellerData,
    sellerInvoiceTest,
    sellerActiveInvoice,
    patchPreApprovedValues,
    patchIcon,
    patchContacts,
    patchBasic,
    patchAddress,
    patchAccount,
    patchInvoice,
    consultCep,
    impersonateSeller,
    patchBlockUser,
    patchBlockBanking,
    getBankingAccounts,
    getCategory,
    patchGhostMode,
    patchAntifraud,
    setSortSellers,
    sortSellers,
    postSeller,
    getTiers,
    patchTiers,
    patchActiveBanking,
    patchAntifraudService,
    sellerCnpjSearch,
    getOwners,
    getOwner,
    postOwner,
    patchOwner,
    deleteOwner,
  };
}
